import React, { useState, useEffect, useContext } from 'react';
import { ThemeContext } from "../../../../../context/ThemeContext";
import { postData, serverURL } from '../../../../../services/Fetchnodeservices';
import { getStoredAdminId } from '../../../../../utils/adminSession';

// ===================== ICONS =====================
const TruckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="1" y="3" width="15" height="13" />
    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
    <circle cx="5.5" cy="18.5" r="2.5" />
    <circle cx="18.5" cy="18.5" r="2.5" />
  </svg>
);

const BankIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <polygon points="12 2 2 7 22 7" />
    <line x1="2" y1="22" x2="22" y2="22" />
    <line x1="4" y1="7" x2="4" y2="22" />
    <line x1="20" y1="7" x2="20" y2="22" />
    <line x1="8" y1="7" x2="8" y2="22" />
    <line x1="12" y1="7" x2="12" y2="22" />
    <line x1="16" y1="7" x2="16" y2="22" />
  </svg>
);

const UserIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const UploadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const WalletIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M22 6v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z" />
    <path d="M22 10h-4a2 2 0 0 0-2 2v0a2 2 0 0 0 2 2h4" />
    <circle cx="18" cy="14" r="1" fill="currentColor" />
  </svg>
);

const TrendingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const FileIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

// ===================== STAT CARD =====================
const StatCard = ({ icon, title, value, color, theme }) => (
  <div
    style={{
      background: theme.surface,
      padding: 20,
      borderRadius: 16,
      boxShadow: `0 4px 12px ${theme.primary}10`,
      border: `1px solid ${theme.border}`,
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = `0 8px 25px ${theme.primary}20`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = `0 4px 12px ${theme.primary}10`;
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: `${color}1a`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: color,
        }}
      >
        {icon}
      </div>
      <h4 style={{ margin: 0, fontSize: 12, color: theme.textSecondary, fontWeight: 500 }}>
        {title}
      </h4>
    </div>
    <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: theme.textPrimary }}>
      {value}
    </h2>
  </div>
);

// ===================== MAIN COMPONENT =====================
export default function BankTransitTracker() {
  const { theme } = useContext(ThemeContext); // ← Theme Context

  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal & Form States
  const [showModal, setShowModal] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [refNo, setRefNo] = useState('');
  const [slipFile, setSlipFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchActiveTrips();
  }, []);

  const fetchActiveTrips = async () => {
    setLoading(true);
    try {
      const res = await postData("vandersuperadmin/admin/cash/active-bank-trips", {});
      if (res.success) {
        setTrips(res.data || []);
      }
    } catch (error) {
      console.error("Fetch Trips Error:", error);
    }
    setLoading(false);
  };

  const handleOpenModal = (trip) => {
    setSelectedTrip(trip);
    setShowModal(true);
    setRefNo('');
    setSlipFile(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTrip(null);
    setRefNo('');
    setSlipFile(null);
  };

  const handleVerifyTripSubmit = async () => {
    if (!refNo || !slipFile) {
      return alert("Please enter Bank Reference ID and select Slip Photo");
    }

    setUploading(true);

    const formData = new FormData();
    formData.append('slip', slipFile);
    formData.append('tripId', selectedTrip.id);
    formData.append('refNo', refNo);

    const adminId = getStoredAdminId();
    if (!adminId) {
      setUploading(false);
      return alert("Admin session is invalid. Please logout and login again.");
    }
    formData.append('adminId', adminId);

    try {
      const response = await fetch(`${serverURL}/vandersuperadmin/admin/cash/verify-bank-slip`, {
        method: 'POST',
        headers: {
          Authorization: `${localStorage.getItem('Token') || ''}`,
        },
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        alert("Bank Deposit Verified Successfully!");
        handleCloseModal();
        fetchActiveTrips();
      } else {
        alert(result.message || "Failed to verify deposit");
      }
    } catch (err) {
      console.error("Upload Error:", err);
      alert(err.message || "Unable to verify bank deposit");
    } finally {
      setUploading(false);
    }
  };

  // Stats calculations
  const totalAmount = trips.reduce((sum, trip) => sum + Number(trip.total_amount || 0), 0);
  const activeTrips = trips.length;

  return (
    <div style={{ padding: 24, minHeight: '100vh', background: theme.background, fontFamily: "'DM Sans', sans-serif", transition: 'background 0.3s ease' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: theme.textPrimary, display: 'flex', alignItems: 'center', gap: 10 }}>
          🚚 Cash In-Transit Tracker
        </h2>
        <p style={{ color: theme.textMuted, margin: '4px 0 0', fontSize: 13 }}>
          Track and verify cash deposits made by staff to the bank.
        </p>
      </div>

      {/* Stats Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <StatCard
          icon={<WalletIcon />}
          title="Total In Transit"
          value={`₹ ${totalAmount.toLocaleString()}`}
          color={theme.warning}
          theme={theme}
        />
        <StatCard
          icon={<TrendingIcon />}
          title="Active Trips"
          value={activeTrips}
          color={theme.primary}
          theme={theme}
        />
        <StatCard
          icon={<BankIcon />}
          title="Target Bank"
          value={trips[0]?.bank_name || 'N/A'}
          color={theme.success}
          theme={theme}
        />
      </div>

      {/* Content */}
      {loading ? (
        <div
          style={{
            textAlign: 'center',
            padding: 60,
            background: theme.surface,
            borderRadius: 16,
            border: `1px solid ${theme.border}`,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              border: `3px solid ${theme.border}`,
              borderTopColor: theme.primary,
              borderRadius: '50%',
              margin: '0 auto 16px',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ color: theme.textSecondary, fontSize: 14 }}>
            Loading active trips...
          </p>
        </div>
      ) : trips.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '80px 20px',
            background: theme.surface,
            border: `1px dashed ${theme.border}`,
            borderRadius: 16,
          }}
        >
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: `${theme.success}1a`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              color: theme.success,
            }}
          >
            <CheckIcon />
          </div>
          <h3 style={{ color: theme.textPrimary, marginBottom: 8, fontSize: 18, fontWeight: 600 }}>
            No Cash Currently in Transit
          </h3>
          <p style={{ color: theme.textMuted, fontSize: 13, margin: 0 }}>
            All handovers have been successfully deposited in the bank.
          </p>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
            gap: 20,
          }}
        >
          {trips.map((trip) => (
            <div
              key={trip.id}
              style={{
                background: theme.surface,
                padding: 24,
                borderRadius: 16,
                border: `1px solid ${theme.border}`,
                boxShadow: `0 4px 12px ${theme.primary}08`,
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `0 8px 25px ${theme.primary}15`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 4px 12px ${theme.primary}08`;
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <span
                    style={{
                      background: `${theme.warning}1a`,
                      color: theme.warning,
                      padding: '4px 12px',
                      borderRadius: 20,
                      fontSize: 11,
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <ClockIcon /> IN TRANSIT
                  </span>
                  <h3 style={{ margin: '12px 0 4px', fontSize: 18, color: theme.textPrimary }}>
                    Trip #{trip.id}
                  </h3>
                  <small style={{ color: theme.textMuted, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <ClockIcon /> {new Date(trip.created_at).toLocaleString()}
                  </small>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: theme.success }}>
                    ₹{Number(trip.total_amount || 0).toLocaleString()}
                  </div>
                  <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                    <BankIcon /> {trip.bank_name || 'Bank'}
                  </div>
                </div>
              </div>

              {/* Staff Info */}
              <div
                style={{
                  borderTop: `1px solid ${theme.border}`,
                  borderBottom: `1px solid ${theme.border}`,
                  paddingTop: 16,
                  paddingBottom: 16,
                  marginBottom: 16,
                }}
              >
                <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <UserIcon />
                  <span style={{ color: theme.textMuted, fontSize: 12 }}>Staff 1:</span>
                  <span style={{ fontWeight: 600, color: theme.textPrimary, fontSize: 13 }}>
                    {trip.staff_name_1 || 'N/A'}
                  </span>
                </div>
                {trip.staff_name_2 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <UserIcon />
                    <span style={{ color: theme.textMuted, fontSize: 12 }}>Staff 2:</span>
                    <span style={{ fontWeight: 600, color: theme.textPrimary, fontSize: 13 }}>
                      {trip.staff_name_2}
                    </span>
                  </div>
                )}
              </div>

              {/* Action Button */}
              <button
                onClick={() => handleOpenModal(trip)}
                style={{
                  width: '100%',
                  padding: 14,
                  background: theme.warning,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  transition: 'all 0.2s ease',
                  boxShadow: `0 4px 12px ${theme.warning}40`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = `0 6px 16px ${theme.warning}60`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = `0 4px 12px ${theme.warning}40`;
                }}
              >
                <UploadIcon /> Upload Slip & Mark Deposited
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Verification Modal */}
      {showModal && selectedTrip && (
        <div
          onClick={handleCloseModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            animation: 'fadeIn 0.2s ease',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: theme.surface,
              padding: 28,
              borderRadius: 20,
              width: 440,
              maxWidth: '90%',
              border: `1px solid ${theme.border}`,
              boxShadow: `0 20px 50px ${theme.primary}30`,
              animation: 'slideUp 0.3s ease',
            }}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: theme.textPrimary }}>
                Verify Bank Deposit
              </h3>
              <button
                onClick={handleCloseModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: theme.textMuted,
                  padding: 4,
                  borderRadius: 6,
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = theme.error || theme.danger}
                onMouseLeave={(e) => e.currentTarget.style.color = theme.textMuted}
              >
                <XIcon />
              </button>
            </div>

            {/* Trip Info */}
            <div
              style={{
                background: theme.surfaceLighter,
                padding: 14,
                borderRadius: 10,
                border: `1px solid ${theme.border}`,
                marginBottom: 20,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: theme.textMuted }}>Trip ID</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: theme.primary, fontFamily: 'monospace' }}>
                  #{selectedTrip.id}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, color: theme.textMuted }}>Amount</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: theme.success }}>
                  ₹{Number(selectedTrip.total_amount || 0).toLocaleString()}
                </span>
              </div>
            </div>

            {/* Reference ID */}
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: theme.textSecondary, display: 'block', marginBottom: 6 }}>
                <FileIcon /> Bank Reference ID / Slip No
              </label>
              <input
                type="text"
                value={refNo}
                onChange={(e) => setRefNo(e.target.value)}
                placeholder="Enter UTR or Receipt No."
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 8,
                  border: `1px solid ${theme.border}`,
                  background: theme.surface,
                  color: theme.textPrimary,
                  fontSize: 13,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = theme.primary}
                onBlur={(e) => e.currentTarget.style.borderColor = theme.border}
              />
            </div>

            {/* File Upload */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: theme.textSecondary, display: 'block', marginBottom: 6 }}>
                <UploadIcon /> Upload Deposit Slip Photo
              </label>
              <div
                style={{
                  border: `2px dashed ${theme.border}`,
                  borderRadius: 8,
                  padding: slipFile ? 12 : 24,
                  textAlign: 'center',
                  background: theme.surfaceLighter,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.background = `${theme.primary}08`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.background = theme.surfaceLighter;
                }}
                onClick={() => document.getElementById('slip-upload').click()}
              >
                <input
                  id="slip-upload"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setSlipFile(e.target.files[0])}
                  style={{ display: 'none' }}
                />
                {slipFile ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <FileIcon />
                    <span style={{ fontSize: 13, color: theme.success, fontWeight: 600 }}>
                      ✓ {slipFile.name}
                    </span>
                  </div>
                ) : (
                  <>
                    <UploadIcon />
                    <p style={{ margin: '8px 0 0', fontSize: 13, color: theme.textMuted }}>
                      Click to upload deposit slip photo
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={handleCloseModal}
                disabled={uploading}
                style={{
                  flex: 1,
                  padding: 12,
                  borderRadius: 10,
                  border: `1px solid ${theme.border}`,
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  background: theme.surface,
                  color: theme.textSecondary,
                  fontSize: 13,
                  fontWeight: 600,
                  transition: 'all 0.2s ease',
                  opacity: uploading ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!uploading) e.currentTarget.style.background = theme.surfaceLighter;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = theme.surface;
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleVerifyTripSubmit}
                disabled={uploading}
                style={{
                  flex: 1,
                  padding: 12,
                  borderRadius: 10,
                  border: 'none',
                  background: uploading ? theme.textMuted : theme.success,
                  color: '#fff',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  fontWeight: 700,
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  boxShadow: uploading ? 'none' : `0 4px 12px ${theme.success}40`,
                  opacity: uploading ? 0.7 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!uploading) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = `0 6px 16px ${theme.success}60`;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = uploading ? 'none' : `0 4px 12px ${theme.success}40`;
                }}
              >
                {uploading ? (
                  <>
                    <div
                      style={{
                        width: 14,
                        height: 14,
                        border: `2px solid #fff`,
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                      }}
                    />
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckIcon /> Confirm Deposit
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
